// 웹 푸시(S06) — 서비스워커 구독을 서버(/api/push/*)에 등록. 발송은 서버 Cron 이 «시작 N분 전»에 한다.
// 서비스워커는 배포본에서만 등록되므로(main.tsx) 개발 서버에선 «지원 안 됨»으로 보인다.
import { getToken } from "../auth";
import { API_BASE, api } from "./api";

export type PushState = "unsupported" | "denied" | "on" | "off";

export const pushSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  return (await navigator.serviceWorker.getRegistration("./")) ?? null;
}

function keyBytes(b64: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export async function pushState(): Promise<PushState> {
  const reg = await registration();
  if (!reg) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const sub = await reg.pushManager.getSubscription();
  return sub && Notification.permission === "granted" ? "on" : "off";
}

/** 권한 요청 → 구독 → 서버 저장. 이미 구독돼 있으면 서버에 다시 알린다(직원이 바뀌었을 수 있음) */
export async function enablePush(): Promise<PushState> {
  const reg = await registration();
  if (!reg) return "unsupported";
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return perm === "denied" ? "denied" : "off";
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { publicKey } = await api<{ publicKey: string }>("/api/push/vapid");
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  }
  await api("/api/push/subscribe", { token: getToken(), body: sub.toJSON() });
  return "on";
}

export async function disablePush(): Promise<PushState> {
  const reg = await registration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    await api("/api/push/unsubscribe", { token: getToken(), body: { endpoint: sub.endpoint } }).catch(() => undefined);
    await sub.unsubscribe();
  }
  return reg ? "off" : "unsupported";
}

export const testPush = () => api<{ sent: number; failed: number; removed: number; subs: number }>("/api/push/test", { token: getToken(), body: {} });

/* ───────── 서버 백업(관리자) ───────── */

export type BackupInfo = { key: string; size: number; at: number };
export const listBackups = () => api<{ backups: BackupInfo[] }>("/api/admin/backups", { token: getToken() }).then((r) => r.backups);
export const runBackup = () => api<{ backup: BackupInfo }>("/api/admin/backups/run", { token: getToken(), body: {} }).then((r) => r.backup);

/** 백업 파일 본문(JSON 문자열) — 내려받기는 화면에서 lib/xlsx download() 로 */
export async function fetchBackup(key: string): Promise<string> {
  const r = await fetch(`${API_BASE}/api/admin/backups/file?key=${encodeURIComponent(key)}`, { headers: { authorization: `Bearer ${getToken()}` }, cache: "no-store" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.text();
}
